import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import '../utils/pdfWorker';
import { useAppContext } from '../context/AppContext';
//import axlLogo from '../assets/axl.png';
import Logo from '../components/Logo';
import { useModal } from '../context/ModalContext';
import { IoMdRefresh } from "react-icons/io";
import { api, handleApiError } from '../utils/api';

interface DeckSuggestionsProps {
  session: any;
}


const DeckSuggestions: React.FC<DeckSuggestionsProps> = ({ session }) => {
  const navigate = useNavigate();
  const { deckFeedback, setDeckFeedback, uploadedFile, applicationId, setAnalysisData, analysisData } = useAppContext();
  const { openPitchDeckTips, openIntakeForm } = useModal();
  
  // Add state for tracking API responses
  const [hasFeedback, setHasFeedback] = useState(false);
  const [hasIntakeForm, setHasIntakeForm] = useState(false);

  // Helper function to check if analysis data is complete
  const checkIfAnalysisComplete = (analysisData: any): boolean => {
    if (!analysisData) return false;
    
    // Check if company_name is filled - this indicates the AI has processed the deck
    const companyName = analysisData.company_name;
    const isComplete = companyName !== null && companyName !== undefined && companyName !== '';

    return isComplete;
  };

  // Add polling effect - Fixed version
  useEffect(() => {
    if (!applicationId) {
      navigate('/');
      return;
    }

    let intervalId: NodeJS.Timeout;
    let isPolling = false;

    const pollEndpoints = async () => {
      if (isPolling) return; // Prevent concurrent polling
      isPolling = true;

      try {
        // Get current state values directly
        const currentDeckFeedback = localStorage.getItem(`deckFeedback_${applicationId}`);
        const currentAnalysisData = localStorage.getItem(`analysisData_${applicationId}`);

        // Poll feedback endpoint only if we don't have feedback
        if (!currentDeckFeedback) {
          const feedbackData = await api.getFeedback(applicationId);
          if (feedbackData.feedback) {
            setDeckFeedback(feedbackData.feedback);
            setHasFeedback(true);
            // Save to localStorage with applicationId prefix for persistence
            localStorage.setItem(`deckFeedback_${applicationId}`, JSON.stringify(feedbackData.feedback));
          }
        }

        // Poll intake form endpoint only if we don't have intake form
        if (!currentAnalysisData) {
          const intakeFormData = await api.getIntakeForm(applicationId);
          if (intakeFormData.intake_form) {
            // Check if the analysis data is actually complete (has meaningful data)
            const isAnalysisComplete = checkIfAnalysisComplete(intakeFormData.intake_form);
            
            if (isAnalysisComplete) {
              setHasIntakeForm(true);
              setAnalysisData(intakeFormData.intake_form);
              // Save to localStorage with applicationId prefix for persistence
              localStorage.setItem(`analysisData_${applicationId}`, JSON.stringify(intakeFormData.intake_form));
            }
          }
        }
      } catch (error) {
        console.error('Error polling endpoints:', handleApiError(error));
      } finally {
        isPolling = false;
      }
    };

    // Check if we already have the data in localStorage for this applicationId
    const checkExistingData = () => {
      // Check for existing feedback
      const storedFeedback = localStorage.getItem(`deckFeedback_${applicationId}`);
      if (storedFeedback) {
        try {
          const parsedFeedback = JSON.parse(storedFeedback);
          setDeckFeedback(parsedFeedback);
          setHasFeedback(true);
        } catch (error) {
          console.error('Error parsing stored feedback:', error);
          localStorage.removeItem(`deckFeedback_${applicationId}`);
        }
      }

      // Check for existing analysis data
      const storedAnalysisData = localStorage.getItem(`analysisData_${applicationId}`);
      if (storedAnalysisData) {
        try {
          const parsedAnalysisData = JSON.parse(storedAnalysisData);
          // Check if the stored data is actually complete
          if (checkIfAnalysisComplete(parsedAnalysisData)) {
            setAnalysisData(parsedAnalysisData);
            setHasIntakeForm(true);
          } else {
            localStorage.removeItem(`analysisData_${applicationId}`); // Remove incomplete data
          }
        } catch (error) {
          console.error('Error parsing stored analysis data:', error);
          localStorage.removeItem(`analysisData_${applicationId}`);
        }
      }
    };

    // Check existing data first
    checkExistingData();

    // Start polling immediately and then continue every 5 seconds
    pollEndpoints(); // Initial poll

    intervalId = setInterval(() => {
      // Check localStorage directly instead of relying on state
      const needsFeedback = !localStorage.getItem(`deckFeedback_${applicationId}`);
      
      // For analysis data, check both existence and completeness
      let needsAnalysisData = true;
      const storedAnalysisData = localStorage.getItem(`analysisData_${applicationId}`);
      if (storedAnalysisData) {
        try {
          const parsedAnalysisData = JSON.parse(storedAnalysisData);
          needsAnalysisData = !checkIfAnalysisComplete(parsedAnalysisData);
        } catch (error) {
          console.error('Error checking stored analysis data:', error);
          localStorage.removeItem(`analysisData_${applicationId}`);
          needsAnalysisData = true;
        }
      }
      
      if (needsFeedback || needsAnalysisData) {
        pollEndpoints();
      } else {
        clearInterval(intervalId);
      }
    }, 5000);

    // Cleanup function
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [applicationId, navigate]); // Removed deckFeedback and analysisData dependencies!

  // Memoize file URL for performance and cleanup
  const pdfFileUrl = useMemo(() => {
    if (!uploadedFile) return null;
    return URL.createObjectURL(uploadedFile);
  }, [uploadedFile]);

  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  // Find feedback for the current slide
  const feedback = deckFeedback?.slides?.find(fb => fb.slideNumber === pageNumber);

  // Add this new function to handle slide selection
  const handleSlideSelect = (pageNum: number) => {
    setPageNumber(pageNum);
    // Use setTimeout to ensure the DOM has updated
    setTimeout(() => {
      const slider = document.querySelector('.slide-preview-container');
      const selectedSlide = document.querySelector(`[data-slide="${pageNum}"]`);
      if (slider && selectedSlide) {
        const sliderRect = slider.getBoundingClientRect();
        const slideRect = selectedSlide.getBoundingClientRect();
        
        // Calculate if the slide is outside the visible area
        if (slideRect.left < sliderRect.left) {
          // Slide is to the left of the visible area
          slider.scrollLeft -= (sliderRect.left - slideRect.left);
        } else if (slideRect.right > sliderRect.right) {
          // Slide is to the right of the visible area
          slider.scrollLeft += (slideRect.right - sliderRect.right);
        }
      }
    }, 0);
  };

  // // Remove the direct navigation check
  // if (!deckFeedback || !uploadedFile) {
  //   return null;
  // }

  return (
    <div className="min-h-screen flex flex-col items-center p-4 px-8 bg-[#F5F5F5]">
      <div className="fixed top-0 left-0 right-0 h-12 bg-black flex items-center pl-8 z-50 w-full justify-between">
        <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
        <Logo background="light" className="h-4 w-auto" onClick={() => navigate('/')} />
        </div>
      </div>
      <div className="w-full max-w-[1600px] flex flex-col gap-6 pt-20">
        {/* Main Content Area */}
        <div className="flex flex-row items-center justify-center">
          <div className="flex items-center gap-6 w-full">
            <p className="text-black text-2xl font-bold font-semibold whitespace-nowrap leading-none ">Review Your Pitch Deck</p>
            <hr className="flex-1 border-t border-gray-300" />
            <div className="flex gap-2">
              <button className="border border-2 border-[#E9E9E9] px-4 py-2 text-[16px] font-semibold rounded-full whitespace-nowrap flex items-center gap-2" onClick={() => navigate('/')}>
                <span>{React.createElement(IoMdRefresh, { className: "w-5 h-5" })}</span>
                Return to Home Page
              </button>
              {/*<div 
                className={`flex items-center cursor-pointer px-4 py-2 text-[16px] font-bold rounded-full gap-2 ${
                  hasIntakeForm 
                    ? 'bg-[#2a2a4a] text-white hover:bg-[#1a1a2a] transition-colors' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                onClick={hasIntakeForm ? openIntakeForm : undefined}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V19.5a2.25 2.25 0 0 0 2.25 2.25h.75m0-3H12m-.75 3h.008v.008h-.008v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                </svg>
                
                Continue to Application
                {!hasIntakeForm && (
                  <svg className="animate-spin h-4 w-4 ml-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
              </div>*/}
            </div>
          </div>
        </div>
        <div className={`flex flex-col lg:flex-row gap-6 ${pageNumber === (numPages ? numPages + 1 : 0) ? 'justify-center' : ''}`}>
          <div className={`${pageNumber === (numPages ? numPages + 1 : 0) ? 'w-full' : 'flex-1'} flex flex-col items-center bg-transparent`}>
            {pdfFileUrl ? (
              pageNumber === (numPages ? numPages + 1 : 0) && hasFeedback ? (
                // Overall Assessment Page
                <div className="w-full max-w-[900px] mx-auto bg-white rounded-lg shadow-[0_0_20px_rgba(0,0,0,0.4)] p-10 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 250px)' }}>
                  <h2 className="text-4xl font-bold text-gray-900 mb-3 text-center">Overall Pitch Deck Assessment</h2>
                  <p className="text-center text-gray-600 mb-8">Comprehensive feedback to improve your pitch deck</p>
                  
                  {deckFeedback?.overallFeedback ? (
                    <div className="prose prose-lg max-w-none">
                      {deckFeedback.overallFeedback.split('\n').map((line, index) => {
                        // Skip empty lines
                        if (!line.trim()) return <div key={index} className="h-4"></div>;
                        
                        // Main headings (numbered sections like "1. Content Strengths")
                        if (/^\d+\.\s+/.test(line)) {
                          return (
                            <h3 key={index} className="text-2xl font-bold text-gray-900 mt-8 mb-4 first:mt-0">
                              {line}
                            </h3>
                          );
                        }
                        
                        // Bullet points (• or ­ characters)
                        if (line.trim().startsWith('•') || line.trim().startsWith('­')) {
                          const content = line.replace(/^[\s•­]+/, '');
                          return (
                            <div key={index} className="ml-6 mb-3 flex items-start">
                              <span className="text-gray-900 mr-3 mt-1">•</span>
                              <span className="text-gray-700 text-base leading-relaxed flex-1">{content}</span>
                            </div>
                          );
                        }
                        
                        // Sub-headings (like "Conclusion")
                        if (line.trim() && !line.startsWith(' ') && line === line.trim() && line.length < 50) {
                          return (
                            <h4 key={index} className="text-xl font-bold text-gray-900 mt-6 mb-3">
                              {line}
                            </h4>
                          );
                        }
                        
                        // Regular paragraphs
                        return (
                          <p key={index} className="text-gray-700 text-base leading-relaxed mb-4">
                            {line}
                          </p>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-12">
                      <p className="text-lg">Overall feedback is being generated...</p>
                    </div>
                  )}
                  
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <p className="text-gray-400 text-lg text-center">
                      <span className="font-normal">Powered by </span>
                      <span className="font-['Borel']">SlideKick</span>
                    </p>
                  </div>
                </div>
              ) : (
                <div className="w-full flex justify-center flex-col items-center">
                  <Document
                    file={pdfFileUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    className="max-w-full"
                    loading={<div className="text-white">Loading PDF...</div>}
                    error={<div className="text-red-400">Error loading PDF!</div>}
                  >
                    <Page
                      pageNumber={pageNumber}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      className="shadow-[0_0_20px_rgba(0,0,0,0.4)] rounded-lg overflow-hidden border border-white/10"
                      width={700}
                      loading={<div className="text-white">Loading page...</div>}
                    />
                    <p className="text-gray-300 text-lg mt-4 text-center">
                      <span className="font-normal">Powered by </span>
                      <span className="font-['Borel']">SlideKick</span>
                    </p>
                  </Document>
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-white opacity-70">
                <p className="text-lg">No PDF file found. Please upload your deck first.</p>
              </div>
            )}
          </div>

          {/* Feedback Section - Side Panel */}
          {pdfFileUrl && pageNumber !== (numPages ? numPages + 1 : 0) ? (
            <div className="flex flex-col h-[500px] w-full">
              <div className="flex flex-col overflow-y-auto flex-1 scrollbar-hide mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-0.5 w-full">
                    <p className="text-gray-700 text-lg font-bold text-left">Summary</p>
                    <div className="h-0.5 bg-gray-300 flex-grow"></div>
                  </div>
                  {!hasFeedback ? (
                    <div className="flex flex-col justify-center items-center py-4 gap-3">
                      <svg className="animate-spin h-8 w-8 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <div className="text-center text-gray-600">
                        {!hasIntakeForm ? (
                          <p className="whitespace-pre-line">
                            Analyzing Pitch Deck ...{'\n'}
                            This process may take 2-5 minutes
                          </p>
                        ) : (
                          <p className="whitespace-pre-line">
                            Almost Done!{'\n'}
                            Generating feedback for your pitch deck ...
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-700 text-base mb-3">{feedback?.summary}</p>
                  )}
                </div>
                
                {hasFeedback && feedback && (feedback.feedback_1_header || feedback.feedback_2_header) && (
                  <div>
                    <div className="flex items-center gap-2 mb-0.5 w-full">
                      <p className="text-gray-700 text-lg font-bold text-left">Feedback</p>
                      <div className="h-0.5 bg-gray-300 flex-grow"></div>
                    </div>
                    <div className="flex flex-col gap-4 px-0 py-2 w-full">
                      {feedback.feedback_1_header && (
                        <div className="rounded-lg border-2 border-gray-300 p-4 text-gray-700 text-base bg-white">
                          <h3 className="font-bold mb-2">{feedback.feedback_1_header}</h3>
                          <p>{feedback.feedback_1_body}</p>
                        </div>
                      )}
                      {feedback.feedback_2_header && (
                        <div className="rounded-lg border-2 border-gray-300 p-4 text-gray-700 text-base bg-white">
                          <h3 className="font-bold mb-2">{feedback.feedback_2_header}</h3>
                          <p>{feedback.feedback_2_body}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex flex-col items-center gap-4 mt-auto">
                <hr className="w-full border-t border-gray-300" />
                {hasFeedback && numPages && (
                  <button 
                    className="border border-2 border-black px-6 py-2.5 text-[16px] font-semibold rounded-full whitespace-nowrap hover:bg-black hover:text-white transition-all duration-200 flex items-center max-w-[400px] mb-2"
                    onClick={() => handleSlideSelect(numPages + 1)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 flex-shrink-0">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V19.5a2.25 2.25 0 0 0 2.25 2.25h.75m0-3H12m-.75 3h.008v.008h-.008v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                    </svg>
                    <span className="truncate ml-2">Overall Assessment</span>
                  </button>
                )}
                <button 
                  className="border border-2 border-[#E9E9E9] px-6 py-2.5 text-[16px] font-semibold rounded-full whitespace-nowrap hover:bg-gray-50 transition-colors flex items-center max-w-[400px] mb-4"
                  onClick={openPitchDeckTips}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 flex-shrink-0">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                  </svg>
                  <span className="truncate ml-2">More Pitch Deck Tips</span>
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {/* Slide Preview Slider */}
        {pdfFileUrl && numPages && (
          <div className="w-full bg-white/95 p-6 rounded-xl shadow-lg">
            <div className="flex items-center gap-4">
              {/* Left Arrow */}
              <button 
                onClick={() => {
                  const slider = document.querySelector('.slide-preview-container');
                  if (slider) {
                    slider.scrollLeft -= 200;
                  }
                }}
                className="flex-shrink-0 w-10 h-10 flex items-center justify-center hover:bg-gray-50 rounded transition-all rounded-full"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 text-gray-600">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </button>

              {/* Preview Container */}
              <div className="flex-1 overflow-hidden">
                <div className="flex gap-4 overflow-x-auto pb-2 hide-scrollbar slide-preview-container">
                  {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => (
                    <button
                      key={pageNum}
                      data-slide={pageNum}
                      onClick={() => handleSlideSelect(pageNum)}
                      className={`flex-shrink-0 w-32 h-20 rounded-lg overflow-hidden border-2 transition-all shadow-sm hover:shadow-md bg-white ${
                        pageNumber === pageNum
                          ? 'border-gray-900 shadow-md'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Document file={pdfFileUrl}>
                        <Page
                          pageNumber={pageNum}
                          width={128}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                      </Document>
                    </button>
                  ))}
                  
                  {/* Overall Assessment Slide */}
                  {hasFeedback && (
                    <button
                      key="summary"
                      data-slide={numPages + 1}
                      onClick={() => handleSlideSelect(numPages + 1)}
                      className={`flex-shrink-0 w-32 h-20 rounded-lg border-2 transition-all shadow-sm hover:shadow-md bg-gradient-to-br from-gray-900 to-gray-700 flex flex-col items-center justify-center ${
                        pageNumber === numPages + 1
                          ? 'border-gray-900 shadow-md'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="white" className="w-8 h-8 mb-1">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V19.5a2.25 2.25 0 0 0 2.25 2.25h.75m0-3H12m-.75 3h.008v.008h-.008v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                      </svg>
                      <span className="text-white text-xs font-semibold">Assessment</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Right Arrow */}
              <button 
                onClick={() => {
                  const slider = document.querySelector('.slide-preview-container');
                  if (slider) {
                    slider.scrollLeft += 200;
                  }
                }}
                className="flex-shrink-0 w-10 h-10 flex items-center justify-center hover:bg-gray-50 rounded-full transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 text-gray-600">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeckSuggestions; 